import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import "../css/RetailersPage.css";
import { getRetailerWalletHistory } from "../api/api";
import { useAuth } from "../context/AuthContext";

interface WalletHistory {
  retailer_wallet_history_id: number;
  retailer_wallet_id: number;
  retailer_id: number;
  amount: number;
  current_balance: number;
  previous_balance: number;
  transaction_type: "credit" | "debit";
  description: string | null;
  created_at: string;
  updated_at: string;
}

interface Pagination {
  total: number;
  count: number;
  per_page: number;
  current_page: number;
  total_pages: number;
  has_more: boolean;
}

export default function RetailersPage() {
  const { user } = useAuth() as any;
  const location = useLocation() as any;

  const selectedRetailerId =
    location?.state?.retailerId ??
    location?.state?.retailerLoginId ??
    location?.state?.login_id ??
    user?.id;

  const selectedRetailerName =
    location?.state?.retailerName || user?.name || "Retailer";

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [history, setHistory] = useState<WalletHistory[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const formatDate = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const fetchHistory = async (
    overridePage = page,
    from = fromDate,
    to = toDate
  ) => {
    if (!user?.token || !selectedRetailerId) {
      setError("Not authenticated");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await getRetailerWalletHistory(
        {
          login_id: selectedRetailerId,
          transaction_type: "",
          search: "",
          from,
          to,
          per_page: perPage,
          page: overridePage,
        },
        user.token
      );

      if (!res?.success) {
        setError(res?.message || "Failed to fetch wallet history.");
        setHistory([]);
        setPagination(null);
        return;
      }

      const payload = res?.data;
      const rows: WalletHistory[] = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : [];

      const apiPagination = payload?.pagination || {};
      const hasPaginationMeta =
        apiPagination.total !== undefined ||
        apiPagination.total_pages !== undefined ||
        apiPagination.current_page !== undefined ||
        payload?.total !== undefined ||
        payload?.total_pages !== undefined ||
        payload?.current_page !== undefined;

      const total = Number(
        apiPagination.total ??
          payload?.total ??
          (hasPaginationMeta
            ? rows.length
            : (overridePage - 1) * perPage + rows.length)
      );
      const currentPage = Number(
        apiPagination.current_page ?? payload?.current_page ?? overridePage
      );
      const pageSize = Number(apiPagination.per_page ?? payload?.per_page ?? perPage);
      const inferredHasMore = rows.length === pageSize;
      const totalPages = Number(
        apiPagination.total_pages ??
          payload?.total_pages ??
          (hasPaginationMeta
            ? Math.max(1, Math.ceil(total / (pageSize || perPage)))
            : currentPage + (inferredHasMore ? 1 : 0))
      );
      const hasMore =
        typeof apiPagination.has_more === "boolean"
          ? apiPagination.has_more
          : hasPaginationMeta
          ? currentPage < totalPages
          : inferredHasMore;

      setHistory(rows);
      setPagination({
        total,
        count: Number(apiPagination.count ?? payload?.count ?? rows.length),
        per_page: pageSize,
        current_page: currentPage,
        total_pages: totalPages,
        has_more: hasMore,
      });
    } catch (err: any) {
      setError(err?.message || "Something went wrong.");
      setHistory([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, [user?.token, selectedRetailerId, page, perPage]);

  const handleSearch = () => {
    setPage(1);
    fetchHistory(1, fromDate, toDate);
  };

  const handlePerPageChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const nextPageSize = Number(event.target.value);
    setPerPage(nextPageSize);
    setPage(1);
  };

  const balanceRows = history.map((item) => ({
    ...item,
    balance: item.current_balance ?? 0,
  }));

  const escapeCsv = (value: string) =>
    '"' + value.split('"').join('""') + '"';

  const exportToCsv = () => {
    if (!balanceRows.length) {
      return;
    }

    const headers = [
      "Transaction ID",
      "Date",
      "Retailer Name",
      "Type",
      "Debit",
      "Credit",
      "Balance",
    ];

    const csvRows = balanceRows.map((item) => [
      "#" + String(item.retailer_wallet_history_id),
      formatDate(item.created_at),
      selectedRetailerName,
      item.transaction_type,
      item.transaction_type === "debit" ? item.amount.toFixed(2) : "-",
      item.transaction_type === "credit" ? item.amount.toFixed(2) : "-",
      item.balance.toFixed(2),
    ]);

    const csvContent = [headers, ...csvRows]
      .map((row) => row.map((cell) => escapeCsv(String(cell))).join(","))
      .join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "retailer_wallet_history_page_" + String(page) + ".csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const isSpecificRetailerView = Boolean(
    location?.state?.retailerLoginId ||
    location?.state?.retailerId ||
    location?.state?.login_id
  );

  return (
    <div className="retailer-page">
      <div className="card">
        <div className="card-header">
          {isSpecificRetailerView ? `Wallet Log - ${selectedRetailerName}` : "Filter Box"}
        </div>
        <div className="card-body">
          <p>
            Below table displays data for a month. You can view data datewise
            using from and to dates.
          </p>
          <div className="filter-row">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
            <select
              value={perPage}
              onChange={handlePerPageChange}
              style={{
                width: 140,
                padding: "10px",
                border: "1px solid #ddd",
                borderRadius: 4,
                background: "#fff",
              }}
            >
              <option value={5}>Page Size: 5</option>
              <option value={10}>Page Size: 10</option>
              <option value={15}>Page Size: 15</option>
              <option value={100}>Page Size: 100</option>
            </select>
            <button className="btn-search" onClick={handleSearch}>
              Search
            </button>
          </div>
        </div>
      </div>

      <div className="card mt-4">
        <div className="card-header card-between">
          <span>Wallet Log</span>
          <button
            className="btn-export"
            onClick={exportToCsv}
            disabled={!balanceRows.length || loading}
            title={balanceRows.length ? "Export current page" : "No records to export"}
            style={{ opacity: balanceRows.length ? 1 : 0.65 }}
          >
            Export To Excel
          </button>
        </div>

        <div className="table-responsive">
          {loading && <p style={{ padding: "1rem" }}>Loading...</p>}
          {error && <p style={{ padding: "1rem", color: "red" }}>{error}</p>}

          {!loading && !error && (
            <table className="log-table">
              <thead>
                <tr>
                  <th>Transaction ID</th>
                  <th>Date</th>
                  <th>Retailer Name</th>
                  <th>Type</th>
                  <th>Debit</th>
                  <th>Credit</th>
                  <th>Balance</th>
                </tr>
              </thead>
              <tbody>
                {balanceRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: "center" }}>
                      No records found.
                    </td>
                  </tr>
                ) : (
                  balanceRows.map((item) => (
                    <tr key={item.retailer_wallet_history_id}>
                      <td>
                        <b>#{item.retailer_wallet_history_id}</b>
                      </td>
                      <td>{formatDate(item.created_at)}</td>
                      <td>{selectedRetailerName}</td>
                      <td>
                        <span
                          style={{
                            color:
                              item.transaction_type === "credit"
                                ? "green"
                                : "red",
                            fontWeight: 600,
                            textTransform: "capitalize",
                          }}
                        >
                          {item.transaction_type}
                        </span>
                      </td>
                      <td>
                        {item.transaction_type === "debit"
                          ? item.amount.toFixed(2)
                          : "-"}
                      </td>
                      <td>
                        {item.transaction_type === "credit"
                          ? item.amount.toFixed(2)
                          : "-"}
                      </td>
                      <td>{item.balance.toFixed(2)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {pagination && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px 16px",
              borderTop: "1px solid #e2e8f0",
              fontSize: 13,
            }}
          >
            <span>
              Page {pagination.current_page} of {pagination.total_pages} - {" "}
              {pagination.total} total records (showing {pagination.per_page} per page)
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                className="btn-search"
                style={{ padding: "6px 14px" }}
                disabled={pagination.current_page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <button
                className="btn-search"
                style={{ padding: "6px 14px" }}
                disabled={!pagination.has_more || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
